/* eslint-disable max-lines */
import { PricePart } from '../enums/PricePart';
import { CurrencyPosition, } from '../models/PlatformConfig';
const getFormatCurrency = (amount, priceArgs, currencyLocale, currencyFormatConfig) => {
    const { currency, currencyPosition } = currencyFormatConfig;
    const numberFormat = new Intl.NumberFormat(currencyLocale, priceArgs);
    const parts = numberFormat.formatToParts(amount);
    if (currency &&
        currencyPosition &&
        [CurrencyPosition.START, CurrencyPosition.END].includes(currencyPosition)) {
        const currencyPartIndex = parts.findIndex(part => part.type === PricePart.CURRENCY);
        if (currencyPartIndex > -1) {
            const currencyPart = parts.splice(currencyPartIndex, 1)[0];
            if (currencyFormatConfig.currencyPosition === CurrencyPosition.START) {
                parts.unshift(currencyPart);
            }
            else {
                parts.push(currencyPart);
            }
        }
    }
    return parts.reduce((price, part) => {
        price += currencyFormatConfig[part.type] || part.value;
        return price;
    }, '');
};
const priceFormatterFactory = {
    create(priceArgs, currencyLocale, separator, decimalSeparator, currencyFormatConfig) {
        // use config management currency formatter if available
        if (currencyFormatConfig) {
            return {
                format(value) {
                    return getFormatCurrency(value, priceArgs, currencyLocale, currencyFormatConfig);
                },
            };
        }
        if (currencyLocale === 'pl-PL') {
            return {
                format: (price) => new Intl.NumberFormat('en', {
                    ...priceArgs,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                })
                    .format(price)
                    .replace(/,+/g, separator),
            };
        }
        if (currencyLocale === 'fi-FI') {
            return {
                format: (price) => {
                    let formattedPrice = new Intl.NumberFormat(currencyLocale, priceArgs)
                        .format(price)
                        .replace(/\s+/g, ' ');
                    if (decimalSeparator) {
                        formattedPrice = formattedPrice.replace(',', decimalSeparator);
                    }
                    if (price >= 1000 && price < 10000) {
                        formattedPrice = `${formattedPrice.slice(0, 1)}${separator}${formattedPrice.slice(1, formattedPrice.length)}`;
                    }
                    const currencyIndex = formattedPrice.lastIndexOf(' ');
                    const valueWithoutCurrency = currencyIndex > -1
                        ? formattedPrice.slice(0, currencyIndex)
                        : formattedPrice;
                    const priceSplittedThousandParts = valueWithoutCurrency.split(' ');
                    let priceWithoutCurrency = '';
                    priceSplittedThousandParts.forEach((resultPart) => {
                        priceWithoutCurrency = priceWithoutCurrency + resultPart + separator;
                    });
                    if (separator) {
                        priceWithoutCurrency = priceWithoutCurrency.slice(0, priceWithoutCurrency.length - 1);
                    }
                    return currencyIndex > -1
                        ? priceWithoutCurrency + ' ' + formattedPrice.slice(currencyIndex)
                        : priceWithoutCurrency;
                },
            };
        }
        // to remove whitespace in CO currency
        if (currencyLocale === 'es-CO') {
            return {
                format: (price) => new Intl.NumberFormat(currencyLocale, priceArgs)
                    .format(price)
                    .replace(/\s+/g, '')
                    // On Chrome Android instead of $, we get COP
                    .replace('COP', '$'),
            };
        }
        if (currencyLocale === 'es-CL') {
            return {
                format: (price) => new Intl.NumberFormat(currencyLocale, priceArgs)
                    .format(price)
                    .replace(/\s+/g, '')
                    // On Chrome Android instead of $, we get CLP
                    .replace('CLP', '$'),
            };
        }
        // AR currency: add dot when >= 1000 and <10000
        if (currencyLocale === 'es-AR' && priceArgs.currency === 'ARS') {
            return {
                format: (price) => {
                    let result = new Intl.NumberFormat(currencyLocale, priceArgs).format(price);
                    // On iOS it's formatted correctly, it does not need a dot anymore
                    if (price >= 1000 &&
                        price < 10000 &&
                        result.toString().indexOf('.') < 0) {
                        result = `${result.slice(0, result.length - 6)}.${result.slice(result.length - 6)}`;
                    }
                    return result.replace('ARS', '$');
                },
            };
        }
        if (priceArgs.currency === 'HKD') {
            return {
                format: (price) => new Intl.NumberFormat(currencyLocale, priceArgs)
                    .format(price)
                    .replace('HK', ''),
            };
        }
        //@TODO: To be removed once there is actual value and correct currency.
        if (priceArgs.currency === 'AUD' && currencyLocale === 'en-SG') {
            return {
                format: (price) => new Intl.NumberFormat(currencyLocale, priceArgs)
                    .format(price)
                    .replace('A', ''),
            };
        }
        if (priceArgs.currency === 'PEN') {
            return {
                format: (price) => new Intl.NumberFormat(currencyLocale, priceArgs)
                    .format(price)
                    .replace('PEN', 'S/'),
            };
        }
        if (priceArgs.currency === 'BND') {
            return {
                format: (price) => new Intl.NumberFormat(currencyLocale, priceArgs)
                    .format(price)
                    .replace(/\s+/g, '')
                    .replace('BND', 'B$'),
            };
        }
        // On Chrome Android Intl.NumberFormat it doesn't work properly for all country+currency codes
        if (priceArgs.currency === 'USD' && currencyLocale === 'es-PE') {
            return {
                format: (price) => {
                    const result = new Intl.NumberFormat(currencyLocale, priceArgs).format(price);
                    // On Chrome Android Intl.NumberFormat it doesn't work properly for all country+currency codes
                    if (!result.includes('US')) {
                        return result.replace('$', 'US$');
                    }
                    return result.replace(/\s+/g, '').replace('USD', 'US$');
                },
            };
        }
        // On Chrome Android Intl.NumberFormat it doesn't work properly for all country+currency codes
        if (priceArgs.currency === 'USD' && currencyLocale === 'es-AR') {
            return {
                format: (price) => {
                    const result = new Intl.NumberFormat(currencyLocale, priceArgs).format(price);
                    if (!result.includes('US')) {
                        return result.replace(/\s+/g, '').replace('$', 'US$');
                    }
                    return result.replace(/\s+/g, '');
                },
            };
        }
        if (priceArgs.currency === 'USD' &&
            (currencyLocale === 'es-CR' || currencyLocale === 'es-PA')) {
            return {
                format: (price) => {
                    let result = new Intl.NumberFormat(currencyLocale, priceArgs)
                        .format(price)
                        .replace(/\s+/g, '');
                    if (currencyLocale === 'es-CR' && price >= 1000) {
                        if (result.toString().indexOf('.') < 0) {
                            result = `${result.slice(0, result.length - 3)},${result.slice(result.length - 3)}`;
                        }
                        else {
                            result = result.replace('.', ',');
                        }
                    }
                    return result.replace('USD', '$');
                },
            };
        }
        return new Intl.NumberFormat(currencyLocale, priceArgs);
    },
};
export default priceFormatterFactory;
//# sourceMappingURL=priceFormatterFactory.js.map